import { ComponentPortal } from '@angular/cdk/portal';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectorRef, Component, Inject, ViewChild, ElementRef } from '@angular/core';
import { marked } from 'marked';
import { BehaviorSubject, Subscription } from 'rxjs';
import { ComboboxThemeControl } from '../../../appbuilder/controls/combobox/combobox.theme.control';
import { InputswitchThemeControl } from '../../../appbuilder/controls/inputswitch/inputswitch.theme.control';
import { TextboxThemeControl } from '../../../appbuilder/controls/textbox/textbox.theme.control';
import { GenericMenuTab } from '../../../appbuilder/menutabs/generic/generic.menu.tab';
import { BasePanel } from '../../../appbuilder/panels/base.panel';
import { InjectorHelper, TranslateHelper } from '../../../helpers/injector.helper';
import { PropertyGroupDisplay } from '../../../models/enums/propertygroupdisplay.enum';
import { MenuTabLabelPosition } from '../../../models/menutab/menutab.property.model';
import { PROPERTIES, PROPERTYGROUPS } from '../../../services/dynamic.component.service';
import { BasicAuthInterceptor } from '../../../services/basic_auth.interceptor';
import { KIDefinitionService } from '../../../services/kidefinition.service';
import { KISession, KISessionMeta, KISessionService } from '../../../services/kisession.service';
import { LayoutService } from '../../../services/layout.service';
import { SettingsService } from '../../../services/settings.service';
import { IBaseComponent } from '../base.component';

interface ChatMessage {
    role: 'user' | 'assistant' | 'system';
    content: string;
    html?: string;
    elapsedMs?: number;
    // Suggested follow-up prompts parsed out of a "next steps" block in the reply.
    // Derived from content (not persisted) — re-parsed on load. See parseNextSteps().
    nextSteps?: string[];
    // Wall-clock ms when the user pressed Send. Set only for fresh user turns; the server-side
    // KIChatMessage model doesn't persist it (it stores Role + Content + ElapsedMs), so messages
    // restored from a saved session have no sentAt and the hover toolbar omits the time for those.
    sentAt?: number;
}

marked.setOptions({ gfm: true, breaks: true });

function escapeHtml(s: string): string {
    return s
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

// Copy control for a "data card". It's a <span role="button"> because Angular's HTML
// sanitizer strips <button> from [innerHTML]; clicks are handled by delegation in
// onDataCopyClick(). extraClass lets callers position it (e.g. floated into a table corner);
// withLabel=false renders an icon-only control (the click still flashes the check icon).
function dataCopyControl(extraClass = '', withLabel = true): string {
    const cls = 'kichat-data-copy' + (extraClass ? ' ' + extraClass : '');
    return `<span class="${cls}" role="button" tabindex="0" title="Copy" aria-label="Copy">`
        + '<span class="material-icons kichat-data-copy-icon">content_copy</span>'
        + (withLabel ? '<span class="kichat-data-copy-label">Copy</span>' : '')
        + '</span>';
}

// Header bar for a "data card" — a label plus a copy control (used by code blocks).
function dataCardHead(label: string): string {
    return '<div class="kichat-data-head">'
        + `<span class="kichat-data-label">${escapeHtml(label)}</span>`
        + dataCopyControl()
        + '</div>';
}

// Render the structured/"data" parts of a response — fenced code blocks and tables — as
// self-contained cards with a header bar and copy control over a distinctly-shaded body,
// so they stand out from ordinary prose (ChatGPT style). Code copies verbatim; tables
// copy as TSV (see onDataCopyClick) so they paste cleanly into Excel/Sheets.
marked.use({
    renderer: {
        code(code: string, infostring: string | undefined, escaped: boolean): string {
            const lang = (infostring || '').trim().split(/\s+/)[0];
            if (lang === 'mermaid') {
                // A ```mermaid block is a diagram request. We don't render it here: the
                // markdown is re-parsed on every streaming chunk, and a half-generated
                // definition would fail to parse. Instead we keep the source as text;
                // renderPendingMermaid() turns it into SVG once the reply is finalized (and
                // after a saved topic is loaded). "Pending" is detected by the presence of
                // the inner .kichat-mermaid-src node — a class, not a data-* attribute, since
                // Angular's [innerHTML] sanitizer strips data-* attributes. See that method.
                const src = escaped ? code : escapeHtml(code);
                return '<div class="kichat-data kichat-mermaid">'
                    + '<div class="kichat-mermaid-src">' + src + '</div>'
                    + '</div>';
            }
            const body = escaped ? code : escapeHtml(code);
            const langCls = lang ? ` class="language-${escapeHtml(lang)}"` : '';
            return '<div class="kichat-data kichat-code">'
                + dataCardHead(lang || 'text')
                + `<pre><code${langCls}>${body}</code></pre>`
                + '</div>';
        },
        table(header: string, body: string): string {
            const tbody = body ? `<tbody>${body}</tbody>` : '';
            // No header bar for tables — the copy control floats in the top-right corner
            // inside the dashed frame instead.
            return '<div class="kichat-data kichat-table">'
                + dataCopyControl('kichat-table-copy', false)
                + '<div class="kichat-table-scroll">'
                + `<table><thead>${header}</thead>${tbody}</table>`
                + '</div>'
                + '</div>';
        }
    }
});

function renderMarkdown(text: string): string {
    try {
        return marked.parse(text || '', { async: false }) as string;
    } catch {
        return text;
    }
}

// True when a line is just an options/next-steps heading, ignoring markdown decoration
// (heading #, bold **, blockquote >, a leading bullet) and a trailing colon. Umlaut-tolerant
// so both "Mögliche nächste Schritte" and the umlaut-less "Mogliche nachste Schritte" match,
// plus the German offer "Möchten Sie:" and English "Next steps" / "Possible next steps".
// Anchored, so a sentence that merely mentions these words mid-text won't trigger.
function isNextStepsLabel(line: string): boolean {
    const s = line
        .replace(/[#>*_`]/g, '')
        .replace(/^\s*[-+•]\s*/, '')
        .trim()
        .replace(/:\s*$/, '')
        .trim()
        .toLowerCase();
    if (!s) return false;
    return /^(?:m(?:ö|o|oe)gliche\s+)?n(?:ä|a|ae)chste\s+schritte$/.test(s)
        || /^m(?:ö|o|oe)chten\s+sie$/.test(s)
        || /^(?:possible\s+)?next\s+steps$/.test(s);
}

// Returns the text of a markdown list item (-, *, +, or "1." / "1)") with emphasis/code
// markers stripped, or null if the line isn't a list item.
function parseNextStepItem(line: string): string | null {
    const m = line.match(/^\s*(?:[-*+]|\d+[.)])\s+(.*\S)\s*$/);
    if (!m) return null;
    return m[1].replace(/\*\*/g, '').replace(/`/g, '').trim();
}

// Heuristic detection of a "next steps" suggestion block: a label line followed by a
// markdown list. Returns the list items (for clickable chips) plus the content with the
// whole block removed, so the rendered bubble doesn't duplicate what the chips already show.
// content itself is left untouched by callers (copy/persist/regenerate use the full reply).
function parseNextSteps(content: string): { steps: string[]; cleaned: string } {
    const lines = (content || '').split('\n');
    let labelIdx = -1;
    for (let i = 0; i < lines.length; i++) {
        if (isNextStepsLabel(lines[i])) { labelIdx = i; break; }
    }
    if (labelIdx === -1) return { steps: [], cleaned: content };

    let j = labelIdx + 1;
    while (j < lines.length && lines[j].trim() === '') j++; // tolerate a blank line before the list
    const steps: string[] = [];
    let end = labelIdx;
    for (; j < lines.length; j++) {
        const item = parseNextStepItem(lines[j]);
        if (item === null) break;
        steps.push(item);
        end = j;
    }
    if (steps.length === 0) return { steps: [], cleaned: content };

    const cleaned = [...lines.slice(0, labelIdx), ...lines.slice(end + 1)]
        .join('\n')
        .replace(/\n{3,}/g, '\n\n')
        .trim();
    return { steps, cleaned };
}

// --- Duplicate-run collapse (band-aid for upstream GA.KI repetition) ---------------------
// GA.KI's agent loop sometimes streams a planning sentence, then restarts it (often
// truncated) after a tool round, so the reply shows the same block twice back-to-back.
// We collapse only that specific shape and nothing else; see the investigation notes.

const DUP_MIN_RUN = 40;   // a duplicated block must reproduce at least this many exact chars
const DUP_TAIL_SLACK = 24; // ...and the first copy may have at most this much un-reproduced tail

// Candidate block-start positions: string start, plus the index right after a newline or a
// sentence-ending mark (. ! ? :), with or without trailing space — the agent's restarts begin
// at such boundaries (e.g. "…ermitteln.Lass mich…"). Being liberal here only adds candidates;
// the match thresholds below are what actually gate a collapse.
function segmentBoundaries(text: string): number[] {
    const bounds = [0];
    for (let i = 1; i < text.length; i++) {
        const c = text[i - 1];
        if (c === '\n' || c === '.' || c === '!' || c === '?' || c === ':') {
            bounds.push(i);
        }
    }
    return bounds;
}

function commonPrefixLen(text: string, a: number, b: number, max: number): number {
    let r = 0;
    while (r < max && text[a + r] === text[b + r]) r++;
    return r;
}

// One pass: find the most-confident back-to-back repeat and drop its first, orphaned copy.
// For a block [q, p) to qualify, the text restarting at p must reproduce the block's start
// for >= DUP_MIN_RUN chars, and the block's un-reproduced tail (p - q - r) must be tiny —
// that "almost entirely reproduced immediately after itself" test is what distinguishes a
// stutter from two legitimately-similar sentences far apart (whose gap would be large).
function collapseOnce(text: string): string {
    const n = text.length;
    const bounds = segmentBoundaries(text);
    let best: { q: number; p: number; r: number } | null = null;
    for (let i = 0; i < bounds.length; i++) {
        const q = bounds[i];
        for (let j = i + 1; j < bounds.length; j++) {
            const p = bounds[j];
            const max = Math.min(p - q, n - p);
            if (max < DUP_MIN_RUN) continue;
            const r = commonPrefixLen(text, q, p, max);
            if (r >= DUP_MIN_RUN && (p - q - r) <= DUP_TAIL_SLACK && (!best || r > best.r)) {
                best = { q, p, r };
            }
        }
    }
    if (!best) return text;
    // Keep the restart (it continues into the real answer) and anything before the orphan.
    return text.slice(0, best.q) + text.slice(best.p);
}

function collapseRepeatedRuns(text: string): string {
    if (!text || text.length < DUP_MIN_RUN * 2) return text;
    let out = text;
    for (let pass = 0; pass < 4; pass++) { // a few passes to unwind stacked repeats
        const next = collapseOnce(out);
        if (next === out) break;
        out = next;
    }
    return out;
}

@Component({
    selector: 'evi-kichat',
    templateUrl: './kichat.control.html',
    styleUrls: ['./kichat.control.css']
})
export class KIChatControl extends IBaseComponent {
    static Type: any = 'kiChat';
    static Default = {
        Type: 'kiChat',
        Layout: { _Editable: true },
        KIDefinition: null,
        Model: null,
        SystemPrompt: '',
        Temperature: 0.7,
        Streaming: true,
        EmptyHint: '',
        MaxHistoryMessages: 20,
        EnableVoice: false,
        VoiceKIDefinition: null,
        VoiceModel: '',
        Topics: false,
        TopicsLeft: false,
        Context: false,
        DisplayTime: false,
        Diagrams: true,
        ExportHtml: false
    };
    static Meta = {
                Type: 'kiChat',
                Properties: [
                ],
                Events: [
                ]
            };

    // Appended to the system prompt (when the Diagrams property is on) so the model knows it
    // can answer "draw / sketch / chart / visualize …" requests with a Mermaid diagram, which
    // the client then renders to SVG. See renderPendingMermaid() / getMermaid().
    static readonly DIAGRAM_INSTRUCTION =
        'When the user asks you to draw, sketch, chart, or visualize something '
        + '(a diagram, flowchart, sequence, state machine, mind map, org chart, ER diagram, '
        + 'pie chart, gantt or other simple graphic), reply with a Mermaid diagram inside a '
        + 'fenced code block tagged `mermaid`, using valid Mermaid syntax. Keep it simple and '
        + 'readable and add a short explanation. Only produce a diagram when a visual is '
        + 'actually requested or clearly helpful.';

    // Mermaid is large (~hundreds of KB) and only needed once a reply actually contains a
    // diagram, so it's imported lazily on first use and the module is cached process-wide.
    private static mermaidLib: Promise<any> | null = null;
    // Monotonic counter for the unique element id each mermaid.render() call requires.
    private static mermaidSeq = 0;

    @ViewChild('scrollContainer') scrollContainer: ElementRef<HTMLDivElement>;
    @ViewChild('inputArea') inputArea: ElementRef<HTMLTextAreaElement>;

    Messages: ChatMessage[] = [];
    Input = '';
    Sending = false;
    Recording = false;
    LastError: string = null;

    // Context-usage indicator: last prompt_tokens seen on a response, plus the
    // selected model's static ContextLength (from KIDefinition.Models[].ContextLength).
    LastPromptTokens: number = null;
    ContextLength = 0;

    // Live elapsed-time counter for the in-flight query (shown when DisplayTime is on).
    // Ticks once per second; the final value is frozen onto the assistant message's elapsedMs.
    LiveElapsedSec = 0;
    private requestStartMs = 0;
    private timerHandle: any = null;

    // Topics / sessions state
    TopicsOpen = true;
    Sessions: KISessionMeta[] = [];
    CurrentSessionId: string = null;
    // Local, not-yet-persisted topic. Renders as the first entry in the drawer when present.
    // Materialized into a real session by either (a) the user typing a name (SaveSession via
    // commitEditName) or (b) the user sending the first message (auto-create as 'Unsaved').
    Draft: KISessionMeta | null = null;
    // Identifies which row is in inline-rename mode: a session SID, the literal 'draft', or null.
    EditingId: string | null = null;
    NameInput = '';

    // Auto-scroll only while the user is near the bottom of the transcript. Flipped off by
    // onMessagesScroll when they scroll up; flipped back on when they scroll back near bottom.
    // Pressing Send or loading a different topic also re-arms it.
    private stickToBottom = true;
    private readonly STICK_THRESHOLD_PX = 40;

    private streamAbort?: AbortController;
    // Handle for the in-flight non-stream HttpClient subscription so stop() can cancel it.
    // The streaming path uses streamAbort instead (fetch() doesn't go through HttpClient).
    private sendSub?: Subscription;
    private mediaRecorder?: MediaRecorder;
    private recordedChunks: Blob[] = [];

    // History navigation: -1 means "not navigating".
    // Otherwise it indexes into user messages, ordered newest→oldest (0 = most recent).
    // Down past index 0 clears the input.
    private historyIndex = -1;

    constructor(
        cdRef: ChangeDetectorRef,
        @Inject(LayoutService.CONTAINER_DATA) public data,
        private http: HttpClient,
        private sessionService: KISessionService,
        private kiDefService: KIDefinitionService
    ) {
        super(cdRef, data);
    }

    ControlInitialized() {
        if (this.LayoutElement?.Topics) {
            this.loadSessions();
        }
        if (this.LayoutElement?.Context) {
            this.loadContextLength();
        }
    }

    private loadContextLength() {
        const kiId = this.LayoutElement?.KIDefinition;
        const model = this.LayoutElement?.Model;
        if (!kiId || !model) {
            return;
        }
        this.kiDefService.LoadKIDefinition(kiId).subscribe({
            next: (def) => {
                const entry = (def?.Models || []).find(m => m?.Name === model);
                this.ContextLength = Number(entry?.ContextLength) || 0;
                console.log('[KIChat] ContextLength', { kiId, model, entry, ContextLength: this.ContextLength });
                this.cdRef.detectChanges();
            },
            error: (err) => { console.warn('[KIChat] LoadKIDefinition failed', err); }
        });
    }

    loadSessions() {
        this.sessionService.GetSessions().subscribe({
            next: (list) => {
                // Order by CreatedAt (newest topic on top) so a topic keeps a stable slot
                // in the drawer. Sorting by UpdatedAt instead made the active topic jump to
                // the top on every message, since each turn bumps the server-side UpdatedAt.
                this.Sessions = (list || []).sort((a, b) => {
                    const ta = new Date(a.CreatedAt || a.UpdatedAt || 0).getTime();
                    const tb = new Date(b.CreatedAt || b.UpdatedAt || 0).getTime();
                    return tb - ta;
                });
                this.cdRef.detectChanges();
            },
            error: () => {
                this.Sessions = [];
                this.cdRef.detectChanges();
            }
        });
    }

    toggleTopics() {
        this.TopicsOpen = !this.TopicsOpen;
        this.cdRef.detectChanges();
    }

    // Restored sessions come back from the server in BaseObject (PascalCase) shape, and the
    // Role is the numeric KIChatRole enum (0=System, 1=User, 2=Assistant) rather than the
    // lowercase string the chat UI binds to. Normalize both the casing and the role encoding
    // so a reloaded transcript renders — without this the template treats every message as
    // 'assistant' (undefined !== 'user') with empty html, showing blank avatar bubbles.
    private static readonly STORED_ROLES: Array<ChatMessage['role']> = ['system', 'user', 'assistant'];

    private normalizeRole(raw: any): ChatMessage['role'] {
        if (typeof raw === 'number') {
            return KIChatControl.STORED_ROLES[raw] || 'assistant';
        }
        const s = (raw ?? '').toString().toLowerCase();
        if (s === 'user') return 'user';
        if (s === 'system') return 'system';
        return 'assistant';
    }

    // Recompute html + nextSteps from an assistant message's content. When a "next steps"
    // block is found, the chips own it and html is rendered from the cleaned content so the
    // list isn't shown twice; msg.content is left intact for copy/persist/regenerate.
    private finalizeAssistant(msg: ChatMessage) {
        // Drop any back-to-back duplicated block before rendering/persisting, so copy and
        // saved sessions also get the de-duplicated text (not just the bubble).
        msg.content = collapseRepeatedRuns(msg.content);
        const { steps, cleaned } = parseNextSteps(msg.content);
        msg.nextSteps = steps.length ? steps : undefined;
        msg.html = renderMarkdown(steps.length ? cleaned : msg.content);
    }

    private mapStoredMessage(m: any): ChatMessage {
        const role = this.normalizeRole(m?.role ?? m?.Role);
        const content = m?.content ?? m?.Content ?? '';
        const elapsedRaw = m?.elapsedMs ?? m?.ElapsedMs;
        const elapsedMs = typeof elapsedRaw === 'number' ? elapsedRaw : undefined;
        const msg: ChatMessage = { role, content, elapsedMs };
        if (role !== 'user') {
            this.finalizeAssistant(msg);
        }
        return msg;
    }

    selectSession(meta: KISessionMeta) {
        if (!meta?.SID || this.CurrentSessionId === meta.SID) {
            return;
        }
        // Switching away abandons any in-progress draft and inline-rename state.
        this.Draft = null;
        this.EditingId = null;
        this.NameInput = '';
        this.sessionService.LoadSession(meta.SID).subscribe({
            next: (s) => {
                if (!s) {
                    return;
                }
                this.CurrentSessionId = s.SID || meta.SID;
                this.LastError = null;
                this.Messages = (s.Messages || []).map(m => this.mapStoredMessage(m));
                // Loading a different topic resets the viewport context.
                this.stickToBottom = true;
                this.cdRef.detectChanges();
                this.scrollToBottom(true);
                this.renderPendingMermaid();
            },
            error: () => {
                this.LastError = '@@KIChat_LoadFailed';
                this.cdRef.detectChanges();
            }
        });
    }

    newChat() {
        if (this.Sending) {
            return;
        }
        this.Messages = [];
        this.CurrentSessionId = null;
        this.LastError = null;
        // Show an empty placeholder row at the top of the drawer with the cursor in its
        // name input. Materialized into a real session by commitEditName (name typed) or
        // by send() (first message sent without naming → auto-create as 'Unsaved').
        this.Draft = { Name: '' };
        this.EditingId = 'draft';
        this.NameInput = '';
        this.cdRef.detectChanges();
        this.focusRenameInput();
    }

    selectDraft() {
        if (this.Sending) return;
        if (this.EditingId !== 'draft') {
            this.startEditDraft();
        }
    }

    startEditDraft() {
        if (this.Sending || !this.Draft) return;
        this.NameInput = this.Draft.Name || '';
        this.EditingId = 'draft';
        this.cdRef.detectChanges();
        this.focusRenameInput();
    }

    startEditSession(meta: KISessionMeta, ev?: Event) {
        if (this.Sending || !meta?.SID) return;
        if (ev) {
            ev.preventDefault();
            ev.stopPropagation();
        }
        this.NameInput = meta.Name || '';
        this.EditingId = meta.SID;
        this.cdRef.detectChanges();
        this.focusRenameInput();
    }

    private focusRenameInput() {
        setTimeout(() => {
            const input = document.querySelector('.kichat-rename-input') as HTMLInputElement;
            input?.focus();
            input?.select();
        }, 0);
    }

    cancelEditName() {
        // Esc on an unnamed draft → discard the placeholder entirely.
        if (this.EditingId === 'draft' && !(this.NameInput || '').trim() && !(this.Draft?.Name || '').trim()) {
            this.Draft = null;
        }
        this.EditingId = null;
        this.NameInput = '';
        this.cdRef.detectChanges();
    }

    discardDraft(ev: Event) {
        ev.stopPropagation();
        this.Draft = null;
        if (this.EditingId === 'draft') {
            this.EditingId = null;
            this.NameInput = '';
        }
        this.cdRef.detectChanges();
    }

    commitEditName() {
        const editingId = this.EditingId;
        if (!editingId) return;
        const name = (this.NameInput || '').trim();
        if (!name) {
            this.cancelEditName();
            return;
        }

        if (editingId === 'draft') {
            // Materialize the draft. Any messages already on screen are persisted with it
            // (rare — send() handles its own auto-create path, but defends against a future
            // caller that pre-populates Messages before the user names the topic).
            const session: KISession = {
                Name: name,
                KIDefinitionId: this.LayoutElement?.KIDefinition,
                ModelName: this.LayoutElement?.Model,
                SystemPrompt: this.LayoutElement?.SystemPrompt || '',
                Messages: this.Messages.map(m => ({ role: m.role, content: m.content, elapsedMs: m.elapsedMs }))
            };
            this.sessionService.SaveSession(session).subscribe({
                next: (saved) => {
                    this.CurrentSessionId = saved?.SID || null;
                    this.Draft = null;
                    this.EditingId = null;
                    this.NameInput = '';
                    this.loadSessions();
                },
                error: () => {
                    this.LastError = '@@KIChat_SaveFailed';
                    this.cdRef.detectChanges();
                }
            });
            return;
        }

        // Rename an existing session. Always load first: the server-side BaseObject
        // requires both SID and Version on Save, but Version isn't tracked client-side.
        // Round-tripping the loaded object preserves Version (and other BaseObject
        // status fields) as opaque pass-through properties.
        this.sessionService.LoadSession(editingId).subscribe({
            next: (loaded) => {
                if (!loaded) {
                    this.cancelEditName();
                    return;
                }
                loaded.Name = name;
                this.sessionService.SaveSession(loaded).subscribe({
                    next: () => {
                        this.EditingId = null;
                        this.NameInput = '';
                        this.loadSessions();
                    },
                    error: () => {
                        this.LastError = '@@KIChat_SaveFailed';
                        this.cdRef.detectChanges();
                    }
                });
            },
            error: () => {
                this.LastError = '@@KIChat_LoadFailed';
                this.cdRef.detectChanges();
            }
        });
    }

    deleteSession(meta: KISessionMeta, ev: Event) {
        ev.stopPropagation();
        if (!meta?.SID) {
            return;
        }
        this.sessionService.DeleteSession(meta.SID).subscribe({
            next: () => {
                if (this.CurrentSessionId === meta.SID) {
                    // Reset to the no-session state directly — don't go through newChat()
                    // since the user didn't ask to start a named topic; the next message
                    // should auto-create as 'Unsaved' like the fresh-load path.
                    this.Messages = [];
                    this.CurrentSessionId = null;
                    this.LastError = null;
                }
                this.loadSessions();
            },
            error: () => {
                this.LastError = '@@KIChat_DeleteFailed';
                this.cdRef.detectChanges();
            }
        });
    }

    private persistAppendMessage(msg: ChatMessage) {
        if (!this.LayoutElement?.Topics || !this.CurrentSessionId) {
            return;
        }
        this.sessionService.AddMessage(this.CurrentSessionId, {
            role: msg.role,
            content: msg.content,
            elapsedMs: msg.elapsedMs
        }).subscribe({ error: () => { /* swallow — UI already shows the message */ } });
    }

    onKeyDown(ev: KeyboardEvent) {
        if (ev.key === 'Enter' && !ev.shiftKey) {
            ev.preventDefault();
            this.send();
            return;
        }
        if (ev.key === 'ArrowUp' && this.caretOnFirstLine()) {
            if (this.navigateHistory(1)) ev.preventDefault();
            return;
        }
        if (ev.key === 'ArrowDown' && this.historyIndex !== -1 && this.caretOnLastLine()) {
            if (this.navigateHistory(-1)) ev.preventDefault();
            return;
        }
    }

    // Fired on real user edits only — programmatic Input assignments don't trigger (input),
    // so navigating history won't reset itself.
    onInput() {
        this.historyIndex = -1;
        this.autoGrow();
    }

    private caretOnFirstLine(): boolean {
        const el = this.inputArea?.nativeElement;
        if (!el) return true;
        return el.value.substring(0, el.selectionStart).indexOf('\n') === -1;
    }

    private caretOnLastLine(): boolean {
        const el = this.inputArea?.nativeElement;
        if (!el) return true;
        return el.value.substring(el.selectionEnd).indexOf('\n') === -1;
    }

    private getUserHistory(): string[] {
        // newest first
        const out: string[] = [];
        for (let i = this.Messages.length - 1; i >= 0; i--) {
            if (this.Messages[i].role === 'user') out.push(this.Messages[i].content);
        }
        return out;
    }

    // delta = +1 → older, -1 → newer. Returns true if Input changed.
    private navigateHistory(delta: number): boolean {
        const history = this.getUserHistory();
        if (history.length === 0) return false;

        if (this.historyIndex === -1 && delta < 0) {
            return false; // Down with no active history → let caret behavior happen
        }

        const next = this.historyIndex + delta;
        if (next >= history.length) return false; // past the oldest → ignore

        this.historyIndex = next;
        this.Input = next < 0 ? '' : history[next];
        this.cdRef.detectChanges();

        setTimeout(() => {
            this.autoGrow();
            const el = this.inputArea?.nativeElement;
            if (el) {
                const end = el.value.length;
                el.setSelectionRange(end, end);
            }
        }, 0);
        return true;
    }

    regenerate(assistantIndex: number) {
        if (this.Sending) return;
        let userIdx = -1;
        for (let i = assistantIndex - 1; i >= 0; i--) {
            if (this.Messages[i].role === 'user') { userIdx = i; break; }
        }
        if (userIdx === -1) return;
        // Re-run the original query as a fresh turn at the bottom; the existing
        // response (and the rest of the transcript) stays untouched.
        this.Input = this.Messages[userIdx].content;
        this.send();
    }

    // Re-ask the same query: re-send the original text as a new turn at the bottom,
    // leaving history intact. Mirrors regenerate() but anchored on a user turn.
    retryUserMessage(userIndex: number) {
        if (this.Sending) return;
        const msg = this.Messages[userIndex];
        if (msg?.role !== 'user') return;
        this.Input = msg.content;
        this.send();
    }

    // Copy the query back into the composer for editing. Leaves the transcript intact —
    // the resend (if any) appears as a new turn at the bottom rather than rewriting history.
    editUserMessage(userIndex: number) {
        if (this.Sending) return;
        const msg = this.Messages[userIndex];
        if (msg?.role !== 'user') return;
        this.Input = msg.content;
        this.cdRef.detectChanges();
        setTimeout(() => {
            const el = this.inputArea?.nativeElement;
            if (el) {
                el.focus();
                const end = el.value.length;
                el.setSelectionRange(end, end);
            }
            this.autoGrow();
        }, 0);
    }

    // Clicking a detected "next steps" chip drops the option into the composer (does not
    // auto-send), so the user can edit it or just press Enter to ask.
    useNextStep(text: string) {
        if (this.Sending) return;
        this.Input = (text || '').trim();
        this.cdRef.detectChanges();
        setTimeout(() => {
            const el = this.inputArea?.nativeElement;
            if (el) {
                el.focus();
                const end = el.value.length;
                el.setSelectionRange(end, end);
            }
            this.autoGrow();
        }, 0);
    }

    copyMessage(index: number) {
        const msg = this.Messages[index];
        if (!msg?.content) return;
        navigator.clipboard?.writeText(msg.content).catch(() => { /* clipboard blocked — ignore */ });
    }

    // Name of the active topic, used as the export title/filename. Empty for an unsaved chat.
    private currentTopicName(): string {
        if (this.CurrentSessionId) {
            const s = this.Sessions.find(x => x.SID === this.CurrentSessionId);
            if (s?.Name) return s.Name;
        }
        return (this.Draft?.Name || '').trim();
    }

    private t(key: string): string {
        const tr = TranslateHelper.TranslatorInstance;
        return tr ? tr.instant(key) : key.replace(/^@@/, '');
    }

    // Export the current transcript as a self-contained HTML file. Assistant turns reuse the
    // already-rendered markdown (m.html); user turns are escaped and shown as pre-wrapped text.
    // The embedded stylesheet hides the in-bubble copy controls and styles code/tables so the
    // file stands alone in any browser. Pending (un-rendered) mermaid blocks fall back to their
    // source text — exporting never invokes the diagram renderer.
    exportHtml() {
        const turns = this.Messages.filter(m => m.role !== 'system' && (m.content || m.html));
        if (!turns.length) {
            return;
        }
        const title = this.currentTopicName() || this.t('@@KI Chat');
        const html = this.buildExportDocument(turns, title);

        const safe = (title || 'kichat').replace(/[^\w\-]+/g, '_').replace(/^_+|_+$/g, '') || 'kichat';
        const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
        const filename = `${safe}_${stamp}.html`;

        const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        a.style.display = 'none';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        // Defer revoke so the navigation/download has the URL when it fires.
        setTimeout(() => URL.revokeObjectURL(url), 0);
    }

    private buildExportDocument(turns: ChatMessage[], title: string): string {
        const userLabel = this.t('@@You');
        const assistantLabel = this.t('@@Assistant');
        const rows = turns.map(m => {
            const isUser = m.role === 'user';
            const role = isUser ? userLabel : assistantLabel;
            // User content is plain text → escape and let CSS preserve newlines. Assistant
            // content is already-rendered, sanitized markdown → embed as-is.
            const body = isUser ? escapeHtml(m.content || '') : (m.html || escapeHtml(m.content || ''));
            return '<section class="msg ' + (isUser ? 'user' : 'assistant') + '">'
                + '<div class="role">' + escapeHtml(role) + '</div>'
                + '<div class="content">' + body + '</div>'
                + '</section>';
        }).join('\n');

        const meta = escapeHtml(this.t('@@Exported on') + ' ' + new Date().toLocaleString());
        return '<!DOCTYPE html>\n'
            + '<html>\n<head>\n<meta charset="utf-8">\n'
            + '<meta name="viewport" content="width=device-width, initial-scale=1">\n'
            + '<title>' + escapeHtml(title) + '</title>\n'
            + '<style>' + KIChatControl.EXPORT_CSS + '</style>\n'
            + '</head>\n<body>\n<div class="wrap">\n'
            + '<header class="doc"><h1>' + escapeHtml(title) + '</h1>'
            + '<div class="meta">' + meta + '</div></header>\n'
            + rows
            + '\n</div>\n</body>\n</html>';
    }

    // Self-contained stylesheet for the exported document. Mirrors the chat's code/table cards,
    // hides the interactive copy controls, and keeps a clean light theme that prints well.
    private static readonly EXPORT_CSS = [
        '*{box-sizing:border-box}',
        'body{margin:0;font-family:-apple-system,"Segoe UI",Roboto,Helvetica,Arial,sans-serif;color:#1f2328;background:#f6f8fa;line-height:1.55}',
        '.wrap{max-width:820px;margin:0 auto;padding:32px 20px 64px}',
        'header.doc{margin-bottom:24px;border-bottom:1px solid #d0d7de;padding-bottom:16px}',
        'header.doc h1{margin:0 0 4px;font-size:1.4rem;word-break:break-word}',
        'header.doc .meta{color:#656d76;font-size:.85rem}',
        '.msg{margin:18px 0}',
        '.msg .role{font-size:.72rem;font-weight:700;text-transform:uppercase;letter-spacing:.05em;color:#656d76;margin-bottom:6px}',
        '.msg .content{background:#fff;border:1px solid #d0d7de;border-radius:10px;padding:12px 16px;overflow-x:auto}',
        '.msg.user .content{background:#eef4ff;border-color:#c6d9ff;white-space:pre-wrap;word-break:break-word}',
        '.content>:first-child{margin-top:0}.content>:last-child{margin-bottom:0}',
        'pre{background:#0d1117;color:#e6edf3;padding:12px 14px;border-radius:8px;overflow-x:auto;font-size:.85rem}',
        'pre code{background:none;padding:0;color:inherit}',
        'code{font-family:ui-monospace,SFMono-Regular,Menlo,Consolas,monospace}',
        ':not(pre)>code{background:rgba(175,184,193,.2);padding:.15em .35em;border-radius:6px;font-size:.85em}',
        'table{border-collapse:collapse;margin:8px 0}',
        'th,td{border:1px solid #d0d7de;padding:6px 10px;text-align:left}',
        'th{background:#f6f8fa}',
        '.kichat-data{margin:8px 0}',
        '.kichat-data-copy,.kichat-data-head{display:none!important}',
        '.kichat-mermaid-src{white-space:pre-wrap;font-family:ui-monospace,monospace;background:#f6f8fa;border:1px dashed #d0d7de;border-radius:8px;padding:10px;font-size:.85rem}',
        'a{color:#0969da}',
        'img,svg{max-width:100%}'
    ].join('');

    // Delegated handler for the per-data-block copy controls (code blocks and tables) that
    // marked renders into the bubble. The controls live inside sanitized [innerHTML], so they
    // carry no Angular bindings — we catch the click on the bubble, read the data straight
    // from the DOM (tables as TSV so they paste into a spreadsheet), and flash a check.
    onDataCopyClick(ev: MouseEvent) {
        const btn = (ev.target as HTMLElement)?.closest('.kichat-data-copy') as HTMLElement | null;
        if (!btn) return;
        const card = btn.closest('.kichat-data');
        if (!card) return;
        const table = card.querySelector('table') as HTMLTableElement | null;
        let text: string;
        if (table) {
            text = this.tableToTsv(table);
        } else {
            const codeEl = card.querySelector('pre code') as HTMLElement | null;
            text = codeEl?.innerText ?? codeEl?.textContent ?? '';
        }
        if (!text) return;
        navigator.clipboard?.writeText(text).catch(() => { /* clipboard blocked — ignore */ });
        const icon = btn.querySelector('.kichat-data-copy-icon');
        const label = btn.querySelector('.kichat-data-copy-label');
        if (icon) icon.textContent = 'check';
        if (label) label.textContent = 'Copied';
        btn.classList.add('is-copied');
        setTimeout(() => {
            if (icon) icon.textContent = 'content_copy';
            if (label) label.textContent = 'Copy';
            btn.classList.remove('is-copied');
        }, 1500);
    }

    // Flatten a rendered table to tab-separated rows. Tabs/newlines inside a cell are
    // collapsed to spaces so the column/row structure survives a paste into Excel/Sheets.
    private tableToTsv(table: HTMLTableElement): string {
        return Array.from(table.rows)
            .map(row => Array.from(row.cells)
                .map(cell => (cell.innerText ?? cell.textContent ?? '')
                    .replace(/[\t\r\n]+/g, ' ')
                    .trim())
                .join('\t'))
            .join('\n');
    }

    formatSentAt(ms: number): string {
        if (!ms) return '';
        return new Date(ms).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }

    autoGrow() {
        const el = this.inputArea?.nativeElement;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = Math.min(el.scrollHeight, 200) + 'px';
    }

    send() {
        const text = (this.Input || '').trim();
        if (!text || this.Sending) {
            return;
        }
        const kiId = this.LayoutElement?.KIDefinition;
        const model = this.LayoutElement?.Model;
        if (!kiId || !model) {
            this.LastError = '@@KIChat_NoKIOrModel';
            this.cdRef.detectChanges();
            return;
        }

        // Topics on, no session yet → create one first so AddMessage/loadSessions have a SID.
        // The session name comes from the draft (if the user typed one but didn't commit by
        // hitting Enter) or falls back to the literal 'Unsaved'. We chain send through after
        // SaveSession returns so the rest of the flow runs against a fully-persisted topic.
        if (this.LayoutElement?.Topics && !this.CurrentSessionId) {
            const draftName = (this.NameInput || this.Draft?.Name || '').trim();
            const name = draftName || 'Unsaved';
            this.LastError = null;
            this.Sending = true;
            this.cdRef.detectChanges();
            const session: KISession = {
                Name: name,
                KIDefinitionId: kiId,
                ModelName: model,
                SystemPrompt: this.LayoutElement?.SystemPrompt || '',
                Messages: []
            };
            this.sessionService.SaveSession(session).subscribe({
                next: (saved) => {
                    this.CurrentSessionId = saved?.SID || null;
                    this.Draft = null;
                    this.EditingId = null;
                    this.NameInput = '';
                    this.loadSessions();
                    this.Sending = false;
                    if (!this.CurrentSessionId) {
                        this.LastError = '@@KIChat_SaveFailed';
                        this.cdRef.detectChanges();
                        return;
                    }
                    this.send();
                },
                error: () => {
                    this.LastError = '@@KIChat_SaveFailed';
                    this.Sending = false;
                    this.cdRef.detectChanges();
                }
            });
            return;
        }

        // Sending implies engagement — snap back to the bottom and resume auto-scroll
        // even if the user had scrolled up earlier in the transcript.
        this.stickToBottom = true;
        this.LastError = null;
        const userMsg: ChatMessage = { role: 'user', content: text, sentAt: Date.now() };
        this.Messages.push(userMsg);
        // Append the user turn server-side.
        this.persistAppendMessage(userMsg);
        this.Input = '';
        this.historyIndex = -1;
        this.Sending = true;
        this.startTimer();
        console.log('[KIChat] send', { DisplayTime: this.LayoutElement?.DisplayTime, Streaming: this.LayoutElement?.Streaming });
        this.cdRef.detectChanges();
        if (this.inputArea?.nativeElement) {
            this.inputArea.nativeElement.style.height = 'auto';
        }
        this.scrollToBottom();

        const useStreaming = !!this.LayoutElement.Streaming;
        const messages: ChatMessage[] = [];
        const sysParts: string[] = [];
        const sysPrompt = (this.LayoutElement.SystemPrompt || '').trim();
        if (sysPrompt) {
            sysParts.push(sysPrompt);
        }
        // Teach the model to answer "draw / chart / visualize …" with a Mermaid diagram, which
        // the client renders to SVG (see renderPendingMermaid). On by default; opt out per chat.
        if (this.LayoutElement.Diagrams !== false) {
            sysParts.push(KIChatControl.DIAGRAM_INSTRUCTION);
        }
        if (sysParts.length) {
            messages.push({ role: 'system', content: sysParts.join('\n\n') });
        }
        const cap = Number(this.LayoutElement.MaxHistoryMessages) || 20;
        const history = this.Messages.filter(m => m.role !== 'system');
        history.slice(-cap).forEach(m => messages.push(m));

        const body: any = {
            kiDefinitionId: kiId,
            model: model,
            messages: messages,
            stream: useStreaming
        };
        const temperature = Number(this.LayoutElement.Temperature);
        if (temperature > 0) {
            body.temperature = temperature;
        }
        const maxTokens = Number(this.LayoutElement.MaxTokens);
        if (maxTokens > 0) {
            body.max_tokens = maxTokens;
        }
        // OpenAI-compatible servers only emit a `usage` chunk on streamed responses
        // when stream_options.include_usage is set. Non-stream responses always
        // include usage, so this only matters in the streaming branch.
        if (this.LayoutElement.Context && useStreaming) {
            body.stream_options = { include_usage: true };
        }

        if (useStreaming) {
            this.sendStream(body);
        } else {
            this.sendNonStream(body);
        }
    }

    private sendNonStream(body: any) {
        const url = SettingsService.API_BASE_URL.getValue() + 'api/kichat/completions';
        this.sendSub = this.http.post<any>(url, body).subscribe({
            next: (resp) => {
                this.sendSub = undefined;
                const reply = resp?.choices?.[0]?.message?.content ?? '';
                const elapsedMs = this.stopTimer();
                const assistantMsg: ChatMessage = { role: 'assistant', content: reply, elapsedMs };
                this.finalizeAssistant(assistantMsg);
                this.Messages.push(assistantMsg);
                console.log('[KIChat] non-stream usage', resp?.usage);
                const pt = resp?.usage?.prompt_tokens;
                if (typeof pt === 'number') {
                    this.LastPromptTokens = pt;
                }
                this.Sending = false;
                this.cdRef.detectChanges();
                this.scrollToBottom();
                this.renderPendingMermaid();
                this.persistAfterTurn(assistantMsg);
            },
            error: (err) => {
                this.sendSub = undefined;
                this.stopTimer();
                this.LastError = err?.error?.error?.message || err?.message || 'Request failed';
                this.Sending = false;
                this.cdRef.detectChanges();
            }
        });
    }

    private persistAfterTurn(assistantMsg: ChatMessage) {
        if (!this.LayoutElement?.Topics || !this.CurrentSessionId) {
            return;
        }
        if (assistantMsg.content) {
            this.persistAppendMessage(assistantMsg);
        }
    }

    // Builds the same auth/context headers that BasicAuthInterceptor attaches to HttpClient
    // requests — required because fetch() bypasses Angular's interceptor pipeline.
    private buildAuthHeaders(extra?: Record<string, string>): Record<string, string> {
        const credentialsRaw = localStorage.getItem('credentials');
        let headers: Record<string, string>;
        if (credentialsRaw) {
            const credentials = JSON.parse(credentialsRaw);
            headers = BasicAuthInterceptor.GetHeaders('Basic ' + btoa(credentials.UserName + ':' + credentials.Password));
            headers.AuthProvider = credentials.ProviderId;
        } else {
            const token = localStorage.getItem('token');
            headers = BasicAuthInterceptor.GetHeaders(token ? 'Bearer ' + token : null);
        }
        if (extra) {
            for (const k of Object.keys(extra)) headers[k] = extra[k];
        }
        return headers;
    }

    private async sendStream(body: any) {
        const url = SettingsService.API_BASE_URL.getValue() + 'api/kichat/completions';
        const assistantMsg: ChatMessage = { role: 'assistant', content: '', html: '' };
        this.Messages.push(assistantMsg);
        this.cdRef.detectChanges();

        this.streamAbort = new AbortController();
        try {
            const resp = await fetch(url, {
                method: 'POST',
                headers: this.buildAuthHeaders({
                    'Content-Type': 'application/json',
                    'Accept': 'text/event-stream'
                }),
                body: JSON.stringify(body),
                signal: this.streamAbort.signal
            });
            if (!resp.ok || !resp.body) {
                throw new Error('HTTP ' + resp.status);
            }

            const reader = resp.body.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });

                let boundary;
                while ((boundary = buffer.indexOf('\n\n')) >= 0) {
                    const event = buffer.slice(0, boundary).trim();
                    buffer = buffer.slice(boundary + 2);
                    if (!event.startsWith('data:')) continue;
                    const data = event.slice(5).trim();
                    if (data === '[DONE]') continue;
                    try {
                        const chunk = JSON.parse(data);
                        const delta = chunk?.choices?.[0]?.delta?.content;
                        if (delta) {
                            assistantMsg.content += delta;
                            assistantMsg.html = renderMarkdown(assistantMsg.content);
                            this.cdRef.detectChanges();
                            this.scrollToBottom();
                        }
                        // With stream_options.include_usage, the upstream emits a
                        // final chunk whose `choices` is empty and whose `usage`
                        // carries the token totals for the whole request.
                        if (chunk?.usage) {
                            console.log('[KIChat] stream usage', chunk.usage);
                        }
                        const pt = chunk?.usage?.prompt_tokens;
                        if (typeof pt === 'number') {
                            this.LastPromptTokens = pt;
                            this.cdRef.detectChanges();
                        }
                    } catch {
                        // ignore parse errors (heartbeats / non-JSON shapes)
                    }
                }
            }
        } catch (e: any) {
            if (e?.name === 'AbortError') {
                // cancelled by user / component teardown — leave whatever was streamed
            } else {
                const msg = e?.message || 'Stream failed';
                assistantMsg.content += assistantMsg.content
                    ? `\n\n[Fehler: ${msg}]`
                    : `[Fehler: ${msg}]`;
                assistantMsg.html = renderMarkdown(assistantMsg.content);
                this.LastError = msg;
            }
        } finally {
            assistantMsg.elapsedMs = this.stopTimer();
            // Parse next-steps only on the complete reply — during streaming the block may be
            // half-generated, and chips are hidden while Sending anyway.
            this.finalizeAssistant(assistantMsg);
            this.streamAbort = undefined;
            this.Sending = false;
            this.cdRef.detectChanges();
            this.scrollToBottom();
            this.renderPendingMermaid();
            this.persistAfterTurn(assistantMsg);
        }
    }

    // instant=true forces an immediate jump, overriding the container's CSS
    // scroll-behavior: smooth. Use it when switching topics so the transcript
    // appears already scrolled to the bottom instead of animating top-to-bottom.
    private scrollToBottom(instant = false) {
        if (!this.stickToBottom) return;
        setTimeout(() => {
            const el = this.scrollContainer?.nativeElement;
            if (el) {
                el.scrollTo({ top: el.scrollHeight, behavior: instant ? 'instant' as ScrollBehavior : 'smooth' });
            }
        }, 0);
    }

    onMessagesScroll() {
        const el = this.scrollContainer?.nativeElement;
        if (!el) return;
        const distanceFromBottom = el.scrollHeight - (el.scrollTop + el.clientHeight);
        this.stickToBottom = distanceFromBottom <= this.STICK_THRESHOLD_PX;
    }

    // Lazy-load and one-time-initialize Mermaid. Resolves to the mermaid module, or null if
    // the chunk fails to load (offline / bundle issue) — callers then leave the diagram source
    // visible as a plain-text fallback. securityLevel:'strict' makes mermaid escape all label
    // text and strip click handlers, so the SVG it returns is safe to inject via innerHTML.
    private getMermaid(): Promise<any> {
        if (!KIChatControl.mermaidLib) {
            const dark = !!this.scrollContainer?.nativeElement?.closest('.isDarkTheme');
            KIChatControl.mermaidLib = import('mermaid')
                .then(mod => {
                    const mermaid = (mod as any).default || mod;
                    mermaid.initialize({
                        startOnLoad: false,
                        securityLevel: 'strict',
                        theme: dark ? 'dark' : 'default'
                    });
                    return mermaid;
                })
                .catch(err => {
                    console.warn('[KIChat] mermaid load failed', err);
                    KIChatControl.mermaidLib = null; // allow a later retry
                    return null;
                });
        }
        return KIChatControl.mermaidLib;
    }

    // Turn any pending ```mermaid blocks in the transcript into rendered SVG. Called after a
    // reply is finalized and after a saved topic is loaded — never mid-stream, where a half-
    // generated definition would fail to parse. m.html is stable once finalized, so Angular
    // won't overwrite the nodes we mutate here; if rendering fails the source stays visible.
    private renderPendingMermaid(): void {
        const root = this.scrollContainer?.nativeElement;
        if (!root) return;
        // Pending = a diagram card that still holds its raw source (.kichat-mermaid-src) and
        // hasn't already failed. Rendering replaces the source with the SVG, so a card is
        // processed at most once; failed cards are tagged so we don't retry them every turn.
        const nodes = (Array.from(root.querySelectorAll('.kichat-mermaid')) as HTMLElement[])
            .filter(el => el.querySelector('.kichat-mermaid-src')
                && !el.classList.contains('kichat-mermaid-error'));
        if (!nodes.length) return;
        this.getMermaid().then(mermaid => {
            if (!mermaid) return; // load failed — leave the source text as a fallback
            nodes.forEach(el => {
                const src = (el.querySelector('.kichat-mermaid-src')?.textContent || '').trim();
                if (!src) return;
                const id = 'kichat-mmd-' + (++KIChatControl.mermaidSeq);
                mermaid.render(id, src)
                    .then((res: any) => {
                        el.innerHTML = res?.svg || '';
                        this.scrollToBottom();
                    })
                    .catch((err: any) => {
                        console.warn('[KIChat] mermaid render failed', err);
                        el.classList.add('kichat-mermaid-error'); // keep source visible as fallback
                    });
            });
        });
    }

    // Cancel the in-flight query. Streaming path: abort the fetch (the read loop catches
    // AbortError and leaves whatever was already streamed in place). Non-stream path:
    // unsubscribe from the HttpClient observable so the response is discarded on arrival.
    stop() {
        if (!this.Sending) return;
        this.streamAbort?.abort();
        this.sendSub?.unsubscribe();
        this.sendSub = undefined;
        this.stopTimer();
        this.Sending = false;
        this.cdRef.detectChanges();
    }

    override ngOnDestroy() {
        this.streamAbort?.abort();
        this.sendSub?.unsubscribe();
        this.stopTimer();
        if (this.Recording) {
            this.stopRecording();
        }
        super.ngOnDestroy();
    }

    private startTimer() {
        this.stopTimer();
        this.requestStartMs = Date.now();
        this.LiveElapsedSec = 0;
        this.timerHandle = setInterval(() => {
            this.LiveElapsedSec = Math.floor((Date.now() - this.requestStartMs) / 1000);
            console.log('[KIChat] tick', this.LiveElapsedSec);
            this.cdRef.detectChanges();
        }, 1000);
    }

    private stopTimer(): number {
        const elapsedMs = this.requestStartMs > 0 ? Date.now() - this.requestStartMs : 0;
        if (this.timerHandle) {
            clearInterval(this.timerHandle);
            this.timerHandle = null;
        }
        this.requestStartMs = 0;
        return elapsedMs;
    }

    formatElapsed(ms: number): string {
        if (ms == null || ms < 0) return '';
        const sec = Math.floor(ms / 1000);
        if (sec < 60) return sec + 's';
        const m = Math.floor(sec / 60);
        const s = sec % 60;
        return m + 'm ' + s + 's';
    }

    async toggleRecording() {
        if (!this.LayoutElement?.EnableVoice || this.Sending) return;
        if (this.Recording) {
            this.stopRecording();
            return;
        }
        const voiceKiId = this.LayoutElement.VoiceKIDefinition || this.LayoutElement.KIDefinition;
        if (!voiceKiId) {
            this.LastError = '@@KIChat_NoVoiceKI';
            this.cdRef.detectChanges();
            return;
        }
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            this.recordedChunks = [];
            this.mediaRecorder = new MediaRecorder(stream);
            this.mediaRecorder.ondataavailable = e => {
                if (e.data && e.data.size > 0) this.recordedChunks.push(e.data);
            };
            this.mediaRecorder.onstop = async () => {
                stream.getTracks().forEach(t => t.stop());
                const type = this.recordedChunks[0]?.type || 'audio/webm';
                const blob = new Blob(this.recordedChunks, { type });
                await this.transcribeAndAppend(blob);
            };
            this.mediaRecorder.start();
            this.Recording = true;
            this.LastError = null;
            this.cdRef.detectChanges();
        } catch (e: any) {
            this.LastError = e?.message || 'Microphone access denied';
            this.Recording = false;
            this.cdRef.detectChanges();
        }
    }

    private stopRecording() {
        try {
            this.mediaRecorder?.stop();
        } catch { /* recorder may already be inactive */ }
        this.Recording = false;
        this.cdRef.detectChanges();
    }

    private async transcribeAndAppend(blob: Blob) {
        const url = SettingsService.API_BASE_URL.getValue() + 'api/kichat/transcriptions';
        const fd = new FormData();
        const ext = blob.type.includes('mp4') ? 'm4a' : blob.type.includes('wav') ? 'wav' : 'webm';
        fd.append('file', blob, 'voice.' + ext);
        fd.append('kiDefinitionId', this.LayoutElement.VoiceKIDefinition || this.LayoutElement.KIDefinition);
        const voiceModel = this.LayoutElement.VoiceModel || 'whisper-1';
        fd.append('model', voiceModel);

        try {
            // Don't set Content-Type — fetch() must compute the multipart boundary itself
            const headers = this.buildAuthHeaders();
            delete headers['Content-Type'];
            const resp = await fetch(url, {
                method: 'POST',
                headers,
                body: fd
            });
            if (!resp.ok) throw new Error('HTTP ' + resp.status);
            const json = await resp.json();
            const text = (json?.text || '').trim();
            if (text) {
                this.Input = this.Input ? this.Input + ' ' + text : text;
                this.cdRef.detectChanges();
                setTimeout(() => this.autoGrow(), 0);
            }
        } catch (e: any) {
            this.LastError = e?.message || 'Transcription failed';
            this.cdRef.detectChanges();
        }
    }
}

export class KIChatPanel extends BasePanel {
    static override SIDS = ['a7c8e2f1-3b4d-4e5f-9a8b-1c2d3e4f5a6b'];
    static override ElementType = 'kiChat';

    public static ModelsSub: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
    public static VoiceModelsSub: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
    private static ModelsByKI: { [sid: string]: any[] } = {};
    private static VoiceModelsByKI: { [sid: string]: any[] } = {};

    // Capability bitfield: Chat=1, Embedding=2, VoiceToText=4, VectorQuery=8, Ocr=16
    private static readonly CAP_CHAT = 1;
    private static readonly CAP_VOICETOTEXT = 4;

    static override SetSelectedItem(item) {
        if (item && item.KIDefinition) {
            KIChatPanel.loadModels(item.KIDefinition);
        } else {
            KIChatPanel.ModelsSub.next([]);
        }
        if (item && item.VoiceKIDefinition) {
            KIChatPanel.loadVoiceModels(item.VoiceKIDefinition);
        } else {
            KIChatPanel.VoiceModelsSub.next([]);
        }
    }

    private static loadModels(sid: string) {
        const cached = KIChatPanel.ModelsByKI[sid];
        if (cached) {
            KIChatPanel.ModelsSub.next(cached);
            return;
        }
        const service = InjectorHelper.InjectorInstance.get<KIDefinitionService>(KIDefinitionService);
        service.LoadKIDefinition(sid).subscribe(def => {
            if (!def) {
                KIChatPanel.ModelsSub.next([]);
                return;
            }
            const all = def.Models || [];
            const filtered = all.filter(m => (m.Capabilities & KIChatPanel.CAP_CHAT) !== 0);
            const list = (filtered.length > 0 ? filtered : all).map(m => ({ Name: m.Name, Caption: m.Caption || m.Name }));
            KIChatPanel.ModelsByKI[sid] = list;
            KIChatPanel.ModelsSub.next(list);
        });
    }

    private static loadVoiceModels(sid: string) {
        const cached = KIChatPanel.VoiceModelsByKI[sid];
        if (cached) {
            KIChatPanel.VoiceModelsSub.next(cached);
            return;
        }
        const service = InjectorHelper.InjectorInstance.get<KIDefinitionService>(KIDefinitionService);
        service.LoadKIDefinition(sid).subscribe(def => {
            if (!def) {
                KIChatPanel.VoiceModelsSub.next([]);
                return;
            }
            const all = def.Models || [];
            const filtered = all.filter(m => (m.Capabilities & KIChatPanel.CAP_VOICETOTEXT) !== 0);
            const list = (filtered.length > 0 ? filtered : all).map(m => ({ Name: m.Name, Caption: m.Caption || m.Name }));
            KIChatPanel.VoiceModelsByKI[sid] = list;
            KIChatPanel.VoiceModelsSub.next(list);
        });
    }

    static InitPanel() {
        KIChatPanel.InitSelectedItem();

        PROPERTYGROUPS.push({
            SID: 'a7c8e2f1-3b4d-4e5f-9a8b-1c2d3e4f5a6b',
            ID: 'kiChat',
            Caption: '@@KIChat',
            Index: 100,
            Content: GenericMenuTab,
            Display: PropertyGroupDisplay.Grid,
            Columns: ['100%'],
            Rows: ['auto'],
            CheckVisibility: (item) => item.ElementType == 'kiChat'
        });

        const service = InjectorHelper.InjectorInstance.get<KIDefinitionService>(KIDefinitionService);
        service.GetKIDefinitions('Chat').subscribe(list => {
            PROPERTIES.push({
                ID: 'KIDefinition',
                Parent: 'kiChat',
                Content: new ComponentPortal(ComboboxThemeControl),
                Label: '@@KIAnbindung',
                Row: 1,
                InitArgs: {
                    Placeholder: '@@Select',
                    ItemsSource: list || [],
                    ValueMemberPath: 'SID',
                    DisplayMemberPath: 'Caption',
                    Action: (action) => {
                        const item = action?.SelectedItem;
                        if (item && item.KIDefinition) {
                            KIChatPanel.loadModels(item.KIDefinition);
                            if (item.Model) {
                                item.Model = null;
                            }
                        } else {
                            KIChatPanel.ModelsSub.next([]);
                        }
                    }
                }
            });
            PROPERTIES.push({
                ID: 'Model',
                Parent: 'kiChat',
                Content: new ComponentPortal(ComboboxThemeControl),
                Label: '@@Model',
                Row: 2,
                InitArgs: {
                    Placeholder: '@@Select',
                    ItemsSourceSub: KIChatPanel.ModelsSub,
                    ValueMemberPath: 'Name',
                    DisplayMemberPath: 'Caption'
                },
                CheckVisibility: (item) => !!item.KIDefinition
            });
        });

        PROPERTIES.push({
            ID: 'EmptyHint',
            Parent: 'kiChat',
            Content: new ComponentPortal(TextboxThemeControl),
            Label: '@@Initial Greeting',
            Row: 3,
            InitArgs: {
                InputType: 'text'
            }
        });
        PROPERTIES.push({
            ID: 'SystemPrompt',
            Parent: 'kiChat',
            Content: new ComponentPortal(TextboxThemeControl),
            Label: '@@SystemPrompt',
            Row: 4,
            InitArgs: {
                InputType: 'text'
            }
        });
        PROPERTIES.push({
            ID: 'Temperature',
            Parent: 'kiChat',
            Content: new ComponentPortal(TextboxThemeControl),
            Label: '@@Temperature',
            Row: 5,
            InitArgs: {
                InputType: 'number'
            }
        });
        PROPERTIES.push({
            ID: 'Streaming',
            Parent: 'kiChat',
            Content: new ComponentPortal(InputswitchThemeControl),
            Label: '@@Streaming',
            LabelPosition: MenuTabLabelPosition.Right,
            Row: 6
        });
        PROPERTIES.push({
            ID: 'MaxHistoryMessages',
            Parent: 'kiChat',
            Content: new ComponentPortal(TextboxThemeControl),
            Label: '@@MaxHistoryMessages',
            Row: 7,
            InitArgs: {
                InputType: 'number'
            }
        });

        PROPERTIES.push({
            ID: 'Topics',
            Parent: 'kiChat',
            Content: new ComponentPortal(InputswitchThemeControl),
            Label: '@@Topics',
            LabelPosition: MenuTabLabelPosition.Right,
            Row: 8
        });

        PROPERTIES.push({
            ID: 'TopicsLeft',
            Parent: 'kiChat',
            Content: new ComponentPortal(InputswitchThemeControl),
            Label: '@@Topics on left side',
            LabelPosition: MenuTabLabelPosition.Right,
            Row: 9,
            CheckVisibility: (item) => !!item.Topics
        });

        PROPERTIES.push({
            ID: 'Context',
            Parent: 'kiChat',
            Content: new ComponentPortal(InputswitchThemeControl),
            Label: '@@Context',
            LabelPosition: MenuTabLabelPosition.Right,
            Row: 10
        });

        PROPERTIES.push({
            ID: 'DisplayTime',
            Parent: 'kiChat',
            Content: new ComponentPortal(InputswitchThemeControl),
            Label: '@@DisplayTime',
            LabelPosition: MenuTabLabelPosition.Right,
            Row: 11
        });

        PROPERTIES.push({
            ID: 'Diagrams',
            Parent: 'kiChat',
            Content: new ComponentPortal(InputswitchThemeControl),
            Label: '@@Diagrams',
            LabelPosition: MenuTabLabelPosition.Right,
            Row: 12
        });

        PROPERTIES.push({
            ID: 'ExportHtml',
            Parent: 'kiChat',
            Content: new ComponentPortal(InputswitchThemeControl),
            Label: '@@Export as HTML',
            LabelPosition: MenuTabLabelPosition.Right,
            Row: 13
        });

        PROPERTIES.push({
            ID: 'EnableVoice',
            Parent: 'kiChat',
            Content: new ComponentPortal(InputswitchThemeControl),
            Label: '@@EnableVoice',
            LabelPosition: MenuTabLabelPosition.Right,
            Row: 14
        });

        service.GetKIDefinitions('VoiceToText').subscribe(list => {
            PROPERTIES.push({
                ID: 'VoiceKIDefinition',
                Parent: 'kiChat',
                Content: new ComponentPortal(ComboboxThemeControl),
                Label: '@@VoiceKIDefinition',
                Row: 15,
                InitArgs: {
                    Placeholder: '@@Select',
                    ItemsSource: list || [],
                    ValueMemberPath: 'SID',
                    DisplayMemberPath: 'Caption',
                    Action: (action) => {
                        const item = action?.SelectedItem;
                        if (item && item.VoiceKIDefinition) {
                            KIChatPanel.loadVoiceModels(item.VoiceKIDefinition);
                            if (item.VoiceModel) {
                                item.VoiceModel = null;
                            }
                        } else {
                            KIChatPanel.VoiceModelsSub.next([]);
                        }
                    }
                },
                CheckVisibility: (item) => !!item.EnableVoice
            });
            PROPERTIES.push({
                ID: 'VoiceModel',
                Parent: 'kiChat',
                Content: new ComponentPortal(ComboboxThemeControl),
                Label: '@@VoiceModel',
                Row: 16,
                InitArgs: {
                    Placeholder: '@@Select',
                    ItemsSourceSub: KIChatPanel.VoiceModelsSub,
                    ValueMemberPath: 'Name',
                    DisplayMemberPath: 'Caption'
                },
                CheckVisibility: (item) => !!item.EnableVoice && !!item.VoiceKIDefinition
            });
        });
    }
}
